"use client";

// Outil d'administration posé sur la page publique d'une formation : il
// (re)pose la fiche de cette formation dans la recherche du site.
//
// Il vit dans le layout des formations, donc sur les vingt et quelques pages
// de formation d'un coup, et se retire de lui-même dès que la page n'est pas
// la fiche d'une formation (une session, le tableau de bord d'un programme).
//
// Textes en français en dur : personne d'autre qu'un administrateur ne les
// voit, comme partout ailleurs dans l'administration.

import { useState } from "react";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { usePathname as useRealPathname } from "next/navigation";
import { usePathname } from "@/i18n/navigation";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function AdminSearchRefresh() {
    const { data: session } = useSession();
    // Deux chemins, deux usages. Celui de next-intl est le chemin *interne*,
    // sans locale ni traduction, et il dit de quelle page il s'agit :
    // « /course/formation-python » sur les fiches sur mesure, mais le gabarit
    // « /course/[slug] » sur toutes les autres. Il sert donc de garde, jamais
    // de source pour le slug — sinon on enverrait « [slug] » au serveur, et
    // précisément sur les formations récentes, celles qu'on veut réparer.
    const route = usePathname();
    // Celui de Next est l'URL réelle : « /fr/formations/formation-claude-code »
    // ou « /en/courses/formation-claude-code ». Le slug n'étant jamais
    // traduit, son dernier segment est le slug dans les deux langues.
    const realPathname = useRealPathname();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const roles = session?.user?.roles || [];
    const isAdmin =
        roles.includes("ROLE_ADMIN") || roles.includes("ROLE_SUPER_ADMIN");

    // Exactement deux segments : la fiche d'une formation, pas une session de
    // lecture ni le tableau de bord d'un programme.
    const segments = (route || "").split("/").filter(Boolean);
    const isCourseSheet = segments.length === 2 && segments[0] === "course";
    const slug = isCourseSheet
        ? (realPathname || "").split("/").filter(Boolean).pop()
        : null;

    if (!isAdmin || !slug || slug.includes("[")) return null;

    const refresh = async () => {
        if (isRefreshing) return;

        setIsRefreshing(true);
        try {
            const response = await fetch("/api/admin/courses/refresh", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ slug }),
            });

            const data = await response.json();

            if (!response.ok) {
                toast.error(data.message || "L'actualisation a échoué.");
                return;
            }

            toast.success(
                `« ${data.title} » est à jour dans la recherche (${data.index}).`,
            );

            // Indexée mais invisible : la fiche existe désormais dans la
            // recherche, la formation, elle, ne s'affichera nulle part.
            if (!data.online) {
                toast.warning("Attention : cette formation est hors ligne.");
            } else if (!data.showInCatalog) {
                toast.warning(
                    "Attention : cette formation est masquée du catalogue.",
                );
            }
        } catch (error) {
            toast.error("L'actualisation a échoué.");
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <button
            onClick={refresh}
            disabled={isRefreshing}
            title={`Actualiser « ${slug} » dans la recherche du site`}
            className="fixed bottom-4 left-4 z-40 flex h-11 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-700 shadow-[0_4px_14px_rgba(0,0,0,0.10)] transition-colors hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-borderDark dark:bg-bgDark-840 dark:text-grayDark-200 dark:hover:border-grayDark-600"
        >
            <SolarIcon
                name={isRefreshing ? "refresh-linear" : "magnifer-linear"}
                className={`h-[18px] w-[18px] text-gray-400 ${
                    isRefreshing ? "animate-spin" : ""
                }`}
            />
            <span className="hidden sm:inline">
                {isRefreshing ? "Actualisation…" : "Actualiser la recherche"}
            </span>
        </button>
    );
}
